import React from 'react'
import { Container, Typography, Box, Chip, Stack, Paper, Divider, Grid, Card, CardContent, Snackbar, alpha, Fade, Skeleton } from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import { useParams, useNavigate } from 'react-router-dom'
import { useGetPoemQuery, useRecordCopyMutation, useRecordShareMutation, useRecordViewMutation, useToggleLikeMutation } from '../../../store/apiSlice'
import SEOHead from '../../../shared/components/SEOHead'
import { SITE_URL } from '../../../config/constants'
import AdSenseWrapper from '../../../shared/components/AdSenseWrapper'
import { sanitizeHtml, textFromHtml } from '../../../lib/sanitize'
import { useTranslation } from 'react-i18next'
import { ArrowBack, Share, Favorite, FavoriteBorder, ContentCopy, Visibility, AutoAwesome, Print } from '@mui/icons-material'
import { IconButton, Button, Tooltip } from '@mui/material'
import PoemErrorState from '../components/PoemErrorState'
import DecorativeTitle from '../../../shared/components/DecorativeTitle'
import BackButton from '../../../shared/components/BackButton'
import ShareDialog from '../../../shared/components/ShareDialog'
import { useAuth } from '../../auth/AuthProvider'

const PoemDetail: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { authenticated } = useAuth()
  const { data: poem, isLoading: loading, error: currentError } = useGetPoemQuery(id || '', { skip: !id })

  const [recordViewFn] = useRecordViewMutation()
  const [recordCopyFn] = useRecordCopyMutation()
  const [recordShareFn] = useRecordShareMutation()
  const [toggleLikeFn] = useToggleLikeMutation()


  React.useEffect(() => {
    // Ensure page scrolls to top whenever a new poem detail is shown
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [id])

  React.useEffect(() => {
    if (!poem?.id) return
    const timer = window.setTimeout(() => {
      recordViewFn(poem.id)
    }, 4000)
    return () => window.clearTimeout(timer)
  }, [recordViewFn, poem?.id])

  const [shareFeedback, setShareFeedback] = React.useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({
    open: false,
    message: '',
    severity: 'info',
  })


  const [shareDialogOpen, setShareDialogOpen] = React.useState(false)
  const handleShare = () => {
    if (!poem) return
    setShareDialogOpen(true)
  }

  if (loading) {
    return (
      <Container sx={{ py: 4, minHeight: '80vh' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rounded" height={80} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Skeleton variant="rounded" height={600} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    )
  }

  if (!poem) {
    const err = currentError as any;
    const status = err?.status
    const variant = status === 404 ? 'notFound' : 'error'
    return (
      <Container sx={{ py: 4 }}>
        <PoemErrorState variant={variant} />
      </Container>
    )
  }

  // Tags are normalized in the Redux slice; ensure array fallback
  const tags: string[] = Array.isArray(poem.tags) ? (poem.tags as string[]) : []

  const description = textFromHtml(poem.content).slice(0, 160)
  const liked = Boolean(poem.likedByCurrentUser)
  const handleLike = () => {
    if (!authenticated) {
      navigate('/login')
      return
    }
    toggleLikeFn(poem.id)
  }

  return (
    <Fade in={true} timeout={600}>
      <Box>
        <SEOHead
        title={poem.title}
        description={description}
        keywords={tags}
        url={`${SITE_URL}/poem/${poem.id}`}
        type="article"
        poem={poem}
        author={poem.author}
        publishedTime={poem.createdAt}
        modifiedTime={poem.updatedAt}
      />

      <Container sx={{ py: 2 }}>
        <Grid container spacing={3}>
          {/* Left Sidebar Ad */}
          <Grid item xs={12} md={2} sx={{ display: { xs: 'none', md: 'block' } }} className="no-print">
            <AdSenseWrapper
              type="placeholder"
              variant="skyscraper"
              position="side"
              sticky={true}
            />
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Printable Area Wrapper */}
            <Box id="printable-poem-area" sx={{ position: 'relative' }}>
              
              {/* Sticky Top AdSense (Mobile) & Decorative Title */}
              <Box sx={{ 
                position: 'sticky', 
                top: { xs: 55, sm: 64 }, 
                zIndex: 10, 
                mb: 1, 
                py: { xs: 1.5, md: 2 },
                width: '100%',
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.95),
                backdropFilter: 'blur(8px)',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}>
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2, textAlign: 'center' }} className="no-print">
                  <AdSenseWrapper
                    type="placeholder"
                    variant="banner"
                    position="top"
                    responsive={true}
                  />
                </Box>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 48 }}>
                  <Box sx={{ position: 'absolute', left: { xs: 8, md: 16 } }} className="no-print">
                    <Tooltip title={t('back', 'Zurück')}>
                      <IconButton 
                        onClick={() => navigate('/')} 
                        sx={{ 
                           color: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
                           bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.08)' : 'rgba(217, 119, 6, 0.08)',
                           '&:hover': {
                             bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.15)' : 'rgba(217, 119, 6, 0.15)',
                           }
                        }}
                      >
                        <ArrowBack />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ flexGrow: 1, px: 6 }}>
                    <DecorativeTitle
                      title={poem.title}
                      layout="column"
                      customFontSize={{ xs: '1.25rem', md: '1.75rem' }}
                      lineWidth={{ xs: 40, md: 80 }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Poem Header */}
              <Card elevation={0} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', position: 'relative', border: (theme) => `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: { xs: 2, md: 3 }, pt: { xs: 1, md: 1 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                  {/* Author with Flanking Lines */}
                  {poem.author && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: 400,
                        gap: 2,
                        mb: 2
                      }}
                    >
                      <Divider className="no-print" sx={{ flexGrow: 1, borderBottomWidth: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.3)' : 'rgba(180, 83, 9, 0.2)' }} />
                      <Typography
                        variant="h6"
                        component="p"
                        sx={{
                          fontWeight: 500,
                          fontSize: '1.1rem',
                          fontFamily: '"Noto Serif", serif',
                          color: 'text.secondary',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {poem.author}
                      </Typography>
                      <Divider className="no-print" sx={{ flexGrow: 1, borderBottomWidth: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.3)' : 'rgba(180, 83, 9, 0.2)' }} />
                    </Box>
                  )}

                  {/* Metadata Chips */}
                  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 2 }} className="no-print">
                    <Chip
                      size="small"
                      label={poem.language === 'ta' ? 'தமிழ்' : 'Latin'}
                      sx={{ borderColor: 'divider', fontWeight: 500 }}
                      variant="outlined"
                    />
                    {poem.createdAt && (
                      <Chip
                        size="small"
                        label={new Date(poem.createdAt).toLocaleDateString()}
                        variant="outlined"
                        sx={{ borderColor: 'divider' }}
                      />
                    )}
                    <Chip
                      size="small"
                      icon={<Visibility fontSize="small" sx={{ fontSize: '1rem !important' }} />}
                      label={`${poem.viewCount ?? 0}`}
                      variant="outlined"
                      sx={{ borderColor: 'divider' }}
                    />
                  </Stack>

                  {/* Action Buttons - Premium Gold/Black/White Theme */}
                  <Box
                    className="no-print"
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      justifyContent: 'center',
                      flexWrap: 'wrap'
                    }}
                  >
                    <Tooltip title={t('share')}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Share fontSize="small" />}
                        onClick={handleShare}
                        sx={{
                          borderRadius: 50,
                          px: 3,
                          py: 0.75,
                          borderColor: (theme) => theme.palette.mode === 'dark' ? '#cd7f32' : '#b45309',
                          color: (theme) => theme.palette.text.primary,
                          borderWidth: '1px',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          '&:hover': {
                            borderColor: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.05)' : 'rgba(217, 119, 6, 0.05)',
                            color: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
                          }
                        }}
                      >
                        {t('share', 'Teilen')}
                      </Button>
                    </Tooltip>

                    <Tooltip title={t('like')}>
                      <Button
                        variant={liked ? 'contained' : 'outlined'}
                        size="small"
                        startIcon={liked ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                        onClick={handleLike}
                        sx={{
                          borderRadius: 50,
                          px: 3,
                          py: 0.75,
                          borderColor: (theme) => liked ? 'transparent' : (theme.palette.mode === 'dark' ? '#cd7f32' : '#b45309'),
                          bgcolor: liked ? '#d97706' : 'transparent',
                          color: liked ? '#fff' : (theme) => theme.palette.text.primary,
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          '&:hover': {
                            bgcolor: liked ? '#b45309' : 'rgba(217, 119, 6, 0.05)',
                            borderColor: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
                            color: liked ? '#fff' : (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
                          }
                        }}
                      >
                        {liked ? 'Geliked' : 'Like'} {typeof poem.likeCount === 'number' && poem.likeCount > 0 ? `(${poem.likeCount})` : ''}
                      </Button>
                    </Tooltip>



                    {/* Print Button */}
                    <Tooltip title={t('print', 'Drucken')}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Print fontSize="small" />}
                        onClick={() => window.print()}
                        sx={{
                          borderRadius: 50,
                          px: 3,
                          py: 0.75,
                          borderColor: (theme) => theme.palette.mode === 'dark' ? '#cd7f32' : '#b45309',
                          color: (theme) => theme.palette.text.primary,
                          borderWidth: '1px',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          '&:hover': {
                            borderColor: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.05)' : 'rgba(217, 119, 6, 0.05)',
                            color: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
                          }
                        }}
                      >
                        {t('print', 'Drucken')}
                      </Button>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>

              {/* Poem Content */}
              <Card 
                elevation={3} 
                sx={{ 
                  borderRadius: 3, 
                  overflow: 'hidden', 
                  minHeight: 400, 
                  display: 'flex', 
                  alignItems: 'center',
                  background: (theme) => theme.palette.mode === 'dark' 
                    ? 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)' 
                    : 'linear-gradient(180deg, rgba(217, 119, 6, 0.03) 0%, rgba(255,255,255,0) 100%)',
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 8 }, width: '100%' }}>
                  <Box
                    sx={{
                      maxWidth: '800px',
                      mx: 'auto',
                      textAlign: 'center',
                      fontFamily: '"Noto Serif", "Georgia", serif',
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      lineHeight: 1.8,
                      letterSpacing: '0.02em',
                      color: (theme) => theme.palette.mode === 'dark' ? '#f3f4f6' : '#1f2937',
                      wordBreak: 'break-word',
                      '& p': {
                        mb: 3, // Spacing between stanzas
                      },
                      '@media print': {
                        fontSize: '12pt !important',
                        lineHeight: '1.6 !important',
                        color: '#000 !important',
                        textAlign: 'left !important',
                      },
                    }}
                    className={poem.language === 'ta' ? 'tamil-text' : ''}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(poem.content) }}
                  />
                </CardContent>
              </Card>

            {/* Tags */}
            {tags.length > 0 && (
              <Box sx={{ mt: 4, textAlign: 'center' }} className="no-print">
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 2, letterSpacing: '0.2em' }}>
                  {t('tags')}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{
                    rowGap: { xs: 1.5, md: 1 },
                    columnGap: { xs: 1.5, md: 1 }
                  }}
                >
                  {tags.map(tag => (
                    <Chip
                      key={tag}
                      label={`#${tag}`}
                      // Custom "Large" size since MUI only supports medium/small
                      sx={{
                        mb: { xs: 1.5, md: 1 },
                        py: 3, // Taller
                        px: 2, // Wider
                        fontSize: '1.2rem', // Larger Text
                        borderRadius: 30, // More rounded/pill
                        borderColor: (theme) => theme.palette.mode === 'dark' ? '#f59e0b' : '#d97706',
                        color: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#b45309',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          paddingLeft: 2,
                          paddingRight: 2,
                        },
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.1)' : 'rgba(217, 119, 6, 0.05)'
                        }
                      }}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Extra padding track so sticky top doesn't get cut off on mobile footer */}
            <Box sx={{ pb: { xs: 20, md: 5 } }} className="no-print" />
            </Box>
          </Grid>

          {/* Right Sidebar Ad */}
          <Grid item xs={12} md={2} sx={{ display: { xs: 'none', md: 'block' } }} className="no-print">
            <AdSenseWrapper
              type="placeholder"
              variant="skyscraper"
              position="side"
              sticky={true}
            />
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={shareFeedback.open}
        autoHideDuration={4000}
        onClose={() => setShareFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          severity={shareFeedback.severity}
          elevation={3}
          variant="filled"
          onClose={() => setShareFeedback((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {shareFeedback.message}
        </MuiAlert>
      </Snackbar>

      {poem && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          url={window.location.href}
          title={poem.title}
          text={description}
          onShareSelect={() => recordShareFn(poem.id)}
        />
      )}
      </Box>
    </Fade>
  )
}

export default PoemDetail
